import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { PageHeader } from "../components/ui/PageHeader";
import { LinkButton } from "../components/ui/Button";
import { CTA } from "../sections/CTA";
import { colors, fonts, media, radius } from "../theme";

const products = [
  {
    id: "invest",
    eyebrow: "Live today",
    title: "Everpesa Invest",
    body: "Our peer to peer lending marketplace. Investors fund vetted, short term loans to small businesses. Borrowers get affordable working capital. Both sides win.",
    bullets: ["Peer to peer marketplace", "Tenors of 30 to 90 days", "Typical 8 to 12% returns per cycle", "Principal protection on every loan"],
    cta: { to: "/invest", label: "Explore Everpesa Invest" },
  },
  {
    id: "sacco",
    eyebrow: "Cooperative banking",
    title: "SACCO Core Banking (SafariBanking)",
    body: "End to end software for cooperatives and SACCOs covering member onboarding, savings and shares, loan origination, repayment, regulatory reporting and statements.",
    bullets: ["Member, savings and loan management", "Mobile money integration", "Compliance grade reports", "Flexible subscription pricing"],
    cta: { to: "/contact", label: "Request a SACCO demo" },
  },
  {
    id: "supplychain",
    eyebrow: "Working capital",
    title: "Supply Chain Finance",
    body: "Invoice and purchase order financing across local supply chains. Suppliers get paid faster, buyers protect their cashflow, and the whole chain keeps moving.",
    bullets: ["Invoice financing", "Purchase order financing", "Anchor buyer onboarding", "Mobile money settlement"],
    cta: { to: "/contact", label: "Talk to our team" },
  },
  {
    id: "grain",
    eyebrow: "Agriculture",
    title: "Grain Voucher System",
    body: "An aggregator across the agricultural value chain. Vouchers, traceable settlement and digital records that connect smallholder farmers, off takers and millers.",
    bullets: ["Digital voucher issuance", "Traceability across the value chain", "Settlement to mobile money", "Off taker dashboards"],
    cta: { to: "/contact", label: "Partner with us" },
  },
  {
    id: "bnpl",
    eyebrow: "Education",
    title: "Ed Tech BNPL",
    body: "A buy now pay later product for school device purchases. Spreads the cost of laptops and learning devices across affordable installments tied to school terms.",
    bullets: ["Device financing for students", "Term based repayment", "Partner with schools and resellers", "Supports digital education"],
    cta: { to: "/contact", label: "Talk to our team" },
  },
];

export const Products = () => (
  <>
    <PageHeader
      eyebrow="Products"
      title="A fintech platform built around small businesses."
      sub="Everpesa centres on a peer to peer marketplace, with a stack of supporting products that put affordable capital and modern tools in the hands of African small businesses, cooperatives, suppliers, farmers and students."
    />

    {products.map((p, i) => (
      <Section key={p.id} id={p.id} style={{ background: i % 2 === 1 ? colors.surfaceAlt : "white" }}>
        <Container>
          <Block $reverse={i % 2 === 1}>
            <Text>
              <Eyebrow>{p.eyebrow}</Eyebrow>
              <h2 style={{ marginTop: 14, marginBottom: 16, fontFamily: fonts.display }}>{p.title}</h2>
              <p style={{ fontSize: 17 }}>{p.body}</p>
              <Bullets>
                {p.bullets.map((b) => <li key={b}><Dot />{b}</li>)}
              </Bullets>
              <LinkButton variant="primary" to={p.cta.to}>{p.cta.label}</LinkButton>
            </Text>
            <Visual $variant={p.id} />
          </Block>
        </Container>
      </Section>
    ))}

    <CTA />
  </>
);

const Block = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  direction: ${(p) => (p.$reverse ? "rtl" : "ltr")};
  > * { direction: ltr; }
  ${media.md} { grid-template-columns: 1fr; gap: 40px; direction: ltr; }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  a { align-self: flex-start; margin-top: 8px; }
`;

const Bullets = styled.ul`
  list-style: none; padding: 0; margin: 6px 0 14px;
  display: flex; flex-direction: column; gap: 10px;
  li { display: flex; align-items: center; gap: 10px; color: ${colors.inkMuted}; font-size: 15.5px; }
`;

const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 50%; background: ${colors.brand};
`;

const Visual = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: ${radius.xl};
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background: ${(p) => {
    if (p.$variant === "invest") return `radial-gradient(600px 400px at 30% 30%, rgba(255,255,255,0.5), transparent 60%), linear-gradient(135deg, ${colors.brand}, ${colors.brandDark})`;
    if (p.$variant === "sacco") return `radial-gradient(400px 300px at 80% 80%, rgba(242,181,61,0.3), transparent 60%), linear-gradient(135deg, #1a3aac, ${colors.brandDark})`;
    if (p.$variant === "grain") return `radial-gradient(400px 300px at 20% 80%, rgba(21,165,114,0.3), transparent 60%), linear-gradient(135deg, ${colors.brandDeep}, #002a8a)`;
    return `radial-gradient(400px 300px at 50% 50%, rgba(255,255,255,0.18), transparent 60%), linear-gradient(135deg, ${colors.brand}, ${colors.brandDeep})`;
  }};

  &::after {
    content: "${(p) => p.$variant.toUpperCase()}";
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Sora', sans-serif;
    font-weight: 800;
    font-size: clamp(2.8rem, 6vw, 5rem);
    color: rgba(255,255,255,0.12);
    letter-spacing: -0.04em;
  }
`;
